import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center space-x-2 text-slate-300 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-slate-300 mt-2">Your privacy is important to us</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8 text-slate-700">
          
          <section>
            <p className="text-base leading-relaxed">
              Your access to and use of this site is subject to the following terms and conditions and all applicable laws. By accessing and using this site, you acknowledge that you have read and accept the terms and conditions, without limitation or qualification.
            </p>
          </section>

          <section>
            <p className="text-sm text-slate-600 mb-3">
              This website is administered by Smartbeeps. At Smartbeeps your privacy is very important to us. The primary purpose of Smartbeeps.com is to be a dynamic resource and business tool to assist you in understanding Smartbeeps offerings and matching your needs and requirements with smartbeeps offerings. It is also utilized as a portal for current and future employees to provide and access information in a secure and controlled way. We want you to feel secure when visiting our site and are committed to maintaining your privacy when doing so.
            </p>
          </section>

          <section>
            <p className="text-sm text-slate-600">
              This Privacy Policy ("Policy") describes and governs our use and disclosure of the information we collect through our website: https://www.smartbeeps.com/ (the "site"), as well as information that we collect through other means about people who contact us, customers, suppliers, and prospective investors and employees ("you" or "your").
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Legal basis for gathering and/or processing your Information:</h2>
            <ul className="space-y-2">
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>To assess the site's effectiveness and usefulness to those who enter the site and search its contents</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>To allow for application to positions posted on the site</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>To periodically send promotional e-mails about our new products and service offerings or other information which we think you may find interesting using the e-mail address that you have provided</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>To allow electronic submission of required paperwork</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>To respond to any query or complaint that you raise with us</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>To maintain our internal records</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Information We Collect:</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Information Provided Directly to Us</h3>
            <p className="text-sm text-slate-600 mb-4">
              Usually, we do not allow users to directly provide us with their information or any "Personal Data" / "Sensitive Personal Information" (as may be defined under the applicable laws) through the site. However, you may provide us directly with information in order to access certain services, to provide services to us or to request further information of Smartbeeps. This information may include your name, contact information, resume information, payment information or other information about yourself.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">Information Collected Indirectly or Automatically</h3>
            <p className="text-sm text-slate-600 mb-4">
              One example of information we collect indirectly is through our Internet access logs. When you access smartbeeps.com, your Internet address is automatically collected and is placed in our Internet access logs. Also, we may use other indirect sources such as third-party analytics providers and technologies, including cookies and similar tools, to assist in collecting similar information, including but not limited to:
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>IP addresses, location information, unique device identifiers, IMEI, and other information about your computer or device(s), browser types, browser language, operating system, mobile device carrier information, and the state or country from which you accessed the site</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Information related to the ways in which you interact with the site, such as referring and exit web pages and URLs, platform type, the number of clicks, domain names, landing pages, pages and content viewed and the order of those pages, statistical information about the use of the site, the amount of time spent on particular pages, the date and time of use, the frequency of your use of the site, error logs, and other similar information</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Legitimate Use and Transfers of Data:</h2>
            <p className="text-sm text-slate-600 mb-4">
              We will ensure that all transfers take place in accordance with the applicable privacy and data protection laws, including by entering into data transfer agreements where necessary. We will not sell individual information and will share it only with our affiliates, subsidiaries and required service providers and/or clients where it is applicable and where consent has been provided by you for such use.
            </p>
            <p className="text-sm text-slate-600 mb-4">
              Your phone number provided to us will be kept strictly confidential. We do not sell, rent, trade, or otherwise share your phone number and SMS consent with any third parties.
            </p>
            <p className="text-sm text-slate-600">
              If Smartbeeps requires Sensitive Personal Information ("Sensitive PII") from you, then smartbeeps will specifically ask for your consent to our proposed uses of such Sensitive PII. The references to "Sensitive PII" are to the various categories of personal data identified by data privacy laws such as Digital Personal Data Protection Act 2023 (DPDPA), including other countries' laws and policies such General Data Protection Regulation "GDPR" of EU law, as requiring special treatment, including in some circumstances the need to obtain explicit consent. These categories may include but are not limited to personal identity numbers, personal data about personality and private life, racial or ethnic origin, nationality, membership of a trade union or profession or trade association, physical or mental health, addictions, property matters, or criminal records (including information about suspected criminal activities).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Safeguard of Your Information:</h2>
            <p className="text-sm text-slate-600">
              We take appropriate measures to maintain the security of your data on Smartbeeps.com. However, you should understand that the open nature of the Internet is such that data may flow over networks without security measures and may be accessed and used by people other than those for whom the data is intended. As a result, while Smartbeeps strives to protect your information, we cannot and do not guarantee that these measures will be 100% effective to secure any information you transmit to or from the site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Retention Period:</h2>
            <p className="text-sm text-slate-600 mb-4">
              We will retain your personal information for as long as needed or permitted in light of the purpose(s) for which it was obtained. The criteria used to determine our retention periods include:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>The length of time we have an ongoing relationship with you and provide the Service to you</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Whether there is a legal obligation to which we are subject</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Whether retention is advisable in light of our legal position (such as in regard to applicable statutes of limitations, litigation, or regulatory investigations)</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Subscribe or Unsubscribe:</h2>
            <p className="text-sm text-slate-600">
              It is completely up to you whether or not you want to access and use this site and provide the information requested, if applicable (generally termed as, "Subscribe"). Smartbeeps uses the contact information you provide to us via sign-up to contact you about our products and services. You may unsubscribe from these communications at any time by clicking the ("Unsubscribe") link in any email that we send to you.
            </p>
          </section>

          <section className="border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Questions About this Policy?</h2>
            <p className="text-sm text-slate-600 mb-4">
              If you have any questions about our Policy, please contact us using the information on our Contact Us page or write to us at info@smartbeeps.com
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                Contact Us
              </Button>
            </Link>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
